/// <reference path="../../env.d.ts" />
import { isTokenExpired } from "../utils/jwt.ts";
import { getToken, setToken, clearSession, logoutAndRedirect, SESSION_KEY } from "../utils/session.ts";
import { ToastService } from "./toast.ts";

export class ApiError extends Error {
    constructor(public override message: string, public status: number) {
        super(message);
        this.name = 'ApiError';
    }
}

export interface ApiRequestOptions extends RequestInit {
    toast?: {
        success?: string;
        error?: string;
        skipSuccess?: boolean;
        skipError?: boolean;
    }
}

export class ApiClient {
    private static instance: ApiClient;
    private API_URL = import.meta.env.API_URL || 'https://api.crate.cc';

    private constructor() { }

    public static getInstance(): ApiClient {
        if (!ApiClient.instance) {
            ApiClient.instance = new ApiClient();
        }
        return ApiClient.instance;
    }

    private consecutiveFailures = 0;
    private FAILURE_THRESHOLD = 5;

    private sessionSwitchPromise: Promise<void> | null = null;

    public setSessionSwitchPromise(promise: Promise<void> | null) {
        this.sessionSwitchPromise = promise;
    }

    public async request<T>(path: string, options?: ApiRequestOptions, raw: boolean = false): Promise<T | Response> {
        // Hold requests if a session switch is in progress, EXCEPT for the session switch request itself
        if (this.sessionSwitchPromise && path !== '/user/session') {
            await this.sessionSwitchPromise;
        }

        const token = getToken();
        const toast = ToastService.getInstance();
        const method = options?.method?.toUpperCase() || 'GET';
        const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

        if (token && isTokenExpired(token)) {
            console.warn('[ApiClient] Token expired locally, logging out...');
            logoutAndRedirect();
            throw new ApiError('Session expired', 401);
        }

        const orgId = localStorage.getItem('crate_org_id');

        // Normalize headers to case-insensitive map
        const headers = new Headers(options?.headers);
        
        if (orgId) {
            headers.set('X-Organization-Id', orgId);
        }

        const publishableKey = import.meta.env.CRATE_PUBLISHABLE_KEY || import.meta.env.PASSWORDLESS_PUBLIC_KEY;
        
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        if (!headers.has('Authorization')) {
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            } else if (publishableKey) {
                headers.set('Authorization', `Bearer ${publishableKey}`);
            }
        }

        const url = path.startsWith('http') ? path : `${this.API_URL}${path}`;

        console.debug(`[ApiClient] Requesting ${path}`, { 
            headers: Object.fromEntries(headers.entries()) 
        });

        try {
            const response = await fetch(url, {
                ...options,
                headers
            });

            if (!response.ok) {
                // Increment failure counter for server errors (5xx)
                if (response.status >= 500) {
                    this.consecutiveFailures++;
                    console.error(`[ApiClient] Server error ${response.status}. Consecutive failures: ${this.consecutiveFailures}`);
                    
                    if (this.consecutiveFailures >= this.FAILURE_THRESHOLD) {
                        console.error('[ApiClient] Multiple server failures detected, logging out...');
                        logoutAndRedirect();
                    }
                }

                if (response.status === 401) {
                    const currentToken = getToken();
                    if (currentToken) {
                        console.warn('[ApiClient] Received 401 Unauthorized with active session, logging out...');
                        logoutAndRedirect();
                    }
                }

                if (response.status === 402) {
                    window.dispatchEvent(new CustomEvent('payment-required', { detail: { path } }));
                }

                const errorText = await response.text();
                let error;
                try {
                    error = JSON.parse(errorText);
                } catch (e) {
                    error = { message: errorText || `Request failed with status ${response.status}` };
                }
                
                if (response.status === 403) {
                    console.error('[ApiClient] 403 Forbidden detected!', {
                        url: url,
                        orgIdHeader: headers.get('X-Organization-ID'),
                        hasToken: !!token
                    });
                    
                    if (error.data?.status === 'unentitled') {
                        if (!options) options = {};
                        if (!options.toast) options.toast = {};
                        options.toast.skipError = true;
                        
                        window.dispatchEvent(new CustomEvent('unentitled-access', { 
                            detail: { 
                                module_id: error.data.module_id,
                                message: error.data.message 
                            } 
                        }));
                    }
                }

                const err = new ApiError(error.error || error.message || `Request failed with status ${response.status}`, response.status);
                
                if (!options?.toast?.skipError) {
                    toast.error(options?.toast?.error || err.message);
                }

                throw err;
            }

            // Success! Reset failure counter
            this.consecutiveFailures = 0;

            // Show success toast ONLY if explicitly requested
            if (isMutation && options?.toast?.success && !options?.toast?.skipSuccess) {
                toast.success(options.toast.success);
            }

            // Check for sliding session refresh token
            const refreshToken = response.headers.get('X-Crate-Token-Refresh');
            if (refreshToken) {
                console.debug('[ApiClient] Sliding session: refreshing local token');
                setToken(refreshToken);
            }

            if (raw) {
                return response;
            }

            if (response.status === 204) {
                return {} as T;
            }

            const data = await response.json();
            // Most endpoints return { data: T }
            // If meta is present, return the whole object to support pagination
            if (data.meta !== undefined) {
                return data;
            }
            return data.data !== undefined ? data.data : data;
        } catch (error: any) {
            // Don't count 4xx as consecutive failures unless it's a specific one we care about
            if (!(error instanceof ApiError)) {
                this.consecutiveFailures++;
                console.error(`[ApiClient] Request to ${path} failed:`, error, `Consecutive failures: ${this.consecutiveFailures}`);
            }
            
            if (this.consecutiveFailures >= this.FAILURE_THRESHOLD) {
                console.error('[ApiClient] Multiple network failures detected, logging out...');
                logoutAndRedirect();
            }

            // Only show toast if it hasn't been shown yet (e.g. not an ApiError that already toasted)
            if (!options?.toast?.skipError && !(error instanceof ApiError)) {
                toast.error(options?.toast?.error || error.message || 'Network error');
            }

            throw error;
        }
    }
}
